import { AuthService } from "../service/AuthService";
import { hasPermission } from "../components/helper/utility";
import { ACTIONS } from "../constants/constants";

export const LIST_SCOPE = {
  ALL: "ALL",
  COMPANY_CODE: "COMPANY_CODE",
  OWN: "OWN",
  NONE: "NONE",
};

/**
 * Priority: VIEW_ALL > VIEW_BY_COMPANY_CODE > VIEW_OWN.
 * Returns NONE when none of the list-scope permissions are assigned.
 */
export const resolveListScope = (module) => {
  if (hasPermission(module, ACTIONS.VIEW_ALL)) {
    return LIST_SCOPE.ALL;
  }
  if (hasPermission(module, ACTIONS.VIEW_BY_COMPANY_CODE)) {
    return LIST_SCOPE.COMPANY_CODE;
  }
  if (hasPermission(module, ACTIONS.VIEW_OWN)) {
    return LIST_SCOPE.OWN;
  }
  return LIST_SCOPE.NONE;
};

export const getCurrentUserCountryKeys = () => {
  const user = AuthService.currentUser();
  if (!user) return [];

  let countries = user.countries;
  if (typeof countries === "string") {
    try {
      countries = JSON.parse(countries);
    } catch {
      countries = [];
    }
  }
  if (!Array.isArray(countries)) return [];

  return [
    ...new Set(
      countries
        .map((c) => {
          if (c == null) return "";
          if (typeof c === "string" || typeof c === "number") {
            return String(c).trim();
          }
          return String(c.countryKey || c.value || c.id || "").trim();
        })
        .filter(Boolean)
    ),
  ];
};

/** Parse user.plant / user.plants from API (array, JSON string, or comma-separated). */
export const parseUserPlantNumbers = (raw) => {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "string" || typeof item === "number") {
          return String(item).trim();
        }
        return String(
          item.plantnumber ||
            item.plantNumber ||
            item.plant ||
            item.value ||
            ""
        ).trim();
      })
      .filter(Boolean);
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parseUserPlantNumbers(parsed);
    } catch {
      // fall through to comma-separated
    }
    return trimmed
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
  }
  return [];
};

/** Plant numbers assigned to the logged-in user (from user.plants or user.plant). */
export const getCurrentUserPlantNumbers = () => {
  const user = AuthService.currentUser();
  if (!user) return [];

  if (Array.isArray(user.plants) && user.plants.length) {
    return [...new Set(parseUserPlantNumbers(user.plants))];
  }

  return [...new Set(parseUserPlantNumbers(user.plant))];
};

export const resolveCompanyCodesFromCountries = (
  companyCountries = [],
  countryKeys = [],
  companyCodeMasters = []
) => {
  if (!countryKeys.length) return [];

  const countryKeySet = new Set(
    countryKeys.map((k) => String(k).trim().toUpperCase())
  );
  const codes = new Set();

  if (Array.isArray(companyCountries)) {
    companyCountries.forEach((row) => {
      const key = String(row?.countryKey || row?.country || "")
        .trim()
        .toUpperCase();
      const companyCode = String(row?.companyCode || "").trim();
      if (key && countryKeySet.has(key) && companyCode) {
        codes.add(companyCode);
      }
    });
  }

  if (Array.isArray(companyCodeMasters)) {
    companyCodeMasters.forEach((row) => {
      const key = String(row?.country || row?.countryKey || "")
        .trim()
        .toUpperCase();
      const companyCode = String(row?.companyCode || "").trim();
      if (key && countryKeySet.has(key) && companyCode) {
        codes.add(companyCode);
      }
    });
  }

  return [...codes];
};

/** Parse user/API company codes — comma-separated string, JSON array, or array. */
export const parseCommaSeparatedCompanyCodes = (raw) => {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) {
    return [
      ...new Set(
        raw.map((code) => String(code).trim()).filter(Boolean)
      ),
    ];
  }

  const text = String(raw).trim();
  if (!text) return [];

  if (text.startsWith("[") || text.startsWith("{")) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parseCommaSeparatedCompanyCodes(parsed);
      }
    } catch {
      // fall through to comma-separated
    }
  }

  return [
    ...new Set(
      text
        .split(",")
        .map((code) => code.trim())
        .filter(Boolean)
    ),
  ];
};

/** Serialize company codes for user.companyCodeSelection (comma-separated). */
export const formatCompanyCodeSelection = (codes = []) =>
  parseCommaSeparatedCompanyCodes(codes).join(",");

export const pickUserCompanyCodeSelectionRaw = (user) =>
  user?.companyCodeSelection ?? user?.companyCodes ?? user?.companyCode ?? "";

/** Company codes saved on the user (companyCodeSelection) or derived from countries. */
export const getCurrentUserCompanyCodes = (companyCountries = []) => {
  const user = AuthService.currentUser();
  if (!user) return [];

  const saved = parseCommaSeparatedCompanyCodes(
    pickUserCompanyCodeSelectionRaw(user)
  );
  if (saved.length) return saved;

  if (Array.isArray(companyCountries) && companyCountries.length) {
    return resolveCompanyCodesFromCountries(
      companyCountries,
      getCurrentUserCountryKeys()
    );
  }

  return [];
};

/**
 * Merge UI filters with permission-based list scope (array companyCode fields).
 */
export const applyListScopeToFilters = (
  filters = {},
  listScope,
  scopedCompanyCodes,
  { ownUserField = "userId" } = {}
) => {
  const next = { ...filters };

  if (listScope === LIST_SCOPE.NONE) {
    return { filters: next, forceEmpty: true };
  }

  if (listScope === LIST_SCOPE.ALL) {
    return { filters: next, forceEmpty: false };
  }

  if (listScope === LIST_SCOPE.COMPANY_CODE) {
    const allowed = Array.isArray(scopedCompanyCodes) ? scopedCompanyCodes : [];
    if (!allowed.length) {
      return { filters: next, forceEmpty: true };
    }

    const allowedSet = new Set(
      allowed.map((c) => String(c).trim().toUpperCase()).filter(Boolean)
    );
    const selected = Array.isArray(next.companyCode) ? next.companyCode : [];
    const selectedNormalized = selected
      .map((c) => String(c).trim())
      .filter(Boolean);

    if (selectedNormalized.length) {
      const intersection = selectedNormalized.filter((c) =>
        allowedSet.has(c.toUpperCase())
      );
      if (!intersection.length) {
        return { filters: next, forceEmpty: true };
      }
      next.companyCode = intersection;
    } else {
      next.companyCode = allowed;
    }
    return { filters: next, forceEmpty: false };
  }

  const currentUserId = AuthService.currentUser()?.id;
  if (currentUserId == null || currentUserId === "") {
    return { filters: next, forceEmpty: true };
  }
  next[ownUserField] = String(currentUserId);
  return { filters: next, forceEmpty: false };
};

/** Scope helper when companyCode is a single string in search state. */
export const applyListScopeToCompanyCodeSearch = (
  search = {},
  listScope,
  scopedCompanyCodes
) => {
  if (listScope === LIST_SCOPE.NONE) {
    return { search, forceEmpty: true };
  }
  if (listScope === LIST_SCOPE.ALL) {
    return { search, forceEmpty: false };
  }

  if (listScope === LIST_SCOPE.COMPANY_CODE) {
    const allowed = Array.isArray(scopedCompanyCodes) ? scopedCompanyCodes : [];
    if (!allowed.length) {
      return { search, forceEmpty: true };
    }

    const allowedSet = new Set(
      allowed.map((c) => String(c).trim().toUpperCase()).filter(Boolean)
    );
    const selected = String(search.companyCode || "").trim();

    if (selected) {
      if (!allowedSet.has(selected.toUpperCase())) {
        return { search, forceEmpty: true };
      }
      return { search, forceEmpty: false };
    }

    return {
      search: { ...search, companyCode: allowed },
      forceEmpty: false,
    };
  }

  const currentUserId = AuthService.currentUser()?.id;
  if (currentUserId == null || currentUserId === "") {
    return { search, forceEmpty: true };
  }
  return {
    search: { ...search, createdBy: String(currentUserId) },
    forceEmpty: false,
  };
};

export const filterOptionsByScopedCompanyCodes = (
  options = [],
  scopedCompanyCodeSet
) => {
  if (!scopedCompanyCodeSet) return options;
  return options.filter((opt) =>
    scopedCompanyCodeSet.has(String(opt.value || "").trim().toUpperCase())
  );
};
